import { useCallback, useEffect, useRef, useState } from 'react';

// พูดแล้วเป็นข้อความ — ใช้ระบบรู้จำเสียงในตัวของ Chrome/Edge (Web Speech API) ซึ่งจะเด้งป๊อปอัปขออนุญาตไมค์ของ
// เบราว์เซอร์เอง ไม่ต้องมี backend/AI key ใช้ซ้ำได้ที่ช่องข้อความไหนก็ได้:
//
//   const { supported, listening, error, start, stop } = useSpeechDictation({
//     getText: () => draft,            // ข้อความที่พิมพ์ไว้แล้ว (เสียงใหม่ต่อท้ายข้อความนี้)
//     onText: (next) => setDraft(next), // ถูกเรียกทุกครั้งที่ได้คำพูดใหม่ (รวมคำที่ยังพูดไม่จบ)
//     lang: 'th-TH',
//   });
//
// หมายเหตุ: ต้องใช้ผ่าน https หรือ localhost, Chrome ส่งเสียงไปประมวลผลที่เซิร์ฟเวอร์ Google (ต้องมีอินเทอร์เน็ต)
// เบราว์เซอร์ที่ไม่รองรับ (เช่น Firefox) supported = false
const ERROR_MESSAGES = {
  'not-allowed': 'ไม่ได้อนุญาตให้ใช้ไมโครโฟน — กดไอคอนกุญแจข้างช่อง URL แล้วอนุญาตไมค์',
  'service-not-allowed': 'ไม่ได้อนุญาตให้ใช้ไมโครโฟน',
  'no-speech': 'ไม่ได้ยินเสียงพูด ลองใหม่อีกครั้ง',
  'audio-capture': 'ไม่พบไมโครโฟนในเครื่อง',
  network: 'ต้องใช้อินเทอร์เน็ตเพื่อแปลงเสียงเป็นข้อความ',
};

export function useSpeechDictation({ getText, onText, lang = 'th-TH' }) {
  const Recognition = typeof window !== 'undefined'
    ? window.SpeechRecognition || window.webkitSpeechRecognition
    : null;
  const recognitionRef = useRef(null);
  const baseRef = useRef('');
  const [listening, setListening] = useState(false);
  const [error, setError] = useState('');

  const stop = useCallback(() => {
    try {
      recognitionRef.current?.stop();
    } catch {
      /* ไม่ได้เริ่มอยู่ก็ไม่เป็นไร */
    }
  }, []);

  const start = useCallback(() => {
    if (!Recognition) {
      setError('เบราว์เซอร์นี้ไม่รองรับการพูดเป็นข้อความ — ใช้ Google Chrome หรือ Edge');
      return;
    }
    setError('');
    const current = (getText?.() || '').replace(/\s+$/, '');
    baseRef.current = current ? `${current} ` : '';

    const recognition = new Recognition();
    recognition.lang = lang;
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.onresult = (event) => {
      // continuous: event.results สะสมทั้งรอบการฟัง — ประกอบใหม่จากต้นรอบทุกครั้งต่อท้ายข้อความเดิม
      let transcript = '';
      for (let i = 0; i < event.results.length; i += 1) {
        transcript += event.results[i][0].transcript;
      }
      onText?.(baseRef.current + transcript);
    };
    recognition.onerror = (event) => {
      setError(ERROR_MESSAGES[event.error] || `ฟังเสียงไม่สำเร็จ (${event.error})`);
    };
    recognition.onend = () => {
      setListening(false);
      recognitionRef.current = null;
    };
    recognitionRef.current = recognition;
    try {
      recognition.start();
      setListening(true);
    } catch (e) {
      setError('เริ่มฟังไม่ได้ ลองใหม่อีกครั้ง');
      setListening(false);
    }
  }, [Recognition, getText, onText, lang]);

  // ปิดไมค์ตอนคอมโพเนนต์ถูกปิด (เช่นปิด dialog)
  useEffect(() => () => {
    try {
      recognitionRef.current?.abort();
    } catch {
      /* noop */
    }
  }, []);

  return { supported: Boolean(Recognition), listening, error, start, stop };
}

export default useSpeechDictation;
